'use client';

import type { SidebarInputMode } from '@/lib/chat/types';
import { Slot } from '@radix-ui/react-slot';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { PanelRight } from 'lucide-react';
import * as React from 'react';
import { useEventCallback } from 'usehooks-ts';

import { focusSidebarChatInput } from './sidebar-chat-input';
import { SIDEBAR_WIDTH_COOKIE_NAME } from './sidebar-width';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import { useChatStore } from '@/lib/stores/chat-store';
import { cn } from '@/lib/utils';

const SIDEBAR_COOKIE_NAME = 'sidebar:state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;
const SIDEBAR_WIDTH = '16rem';
const SIDEBAR_WIDTH_MOBILE = '18rem';
const SIDEBAR_WIDTH_ICON = '3rem';
/** Drag-to-resize constraints */
const SIDEBAR_MIN_WIDTH_PX = 280;
const SIDEBAR_MAX_WIDTH_VW = 50;
const SIDEBAR_DRAG_THRESHOLD_PX = 5;

function getSidebarStartWidthPx(wrapper: HTMLDivElement): number {
  const sidebarElement = wrapper.querySelector<HTMLElement>('[data-sidebar="sidebar"]');
  const measuredWidth = sidebarElement?.getBoundingClientRect().width;
  if (measuredWidth && measuredWidth > 0) {
    return measuredWidth;
  }

  const rawWidth = wrapper.style.getPropertyValue('--sidebar-width').trim();
  const parsedWidth = Number.parseFloat(rawWidth);
  if (!Number.isFinite(parsedWidth)) {
    return SIDEBAR_MIN_WIDTH_PX;
  }

  if (rawWidth.endsWith('px')) {
    return parsedWidth;
  }

  if (rawWidth.endsWith('rem')) {
    const rootFontSize = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
    return Number.isFinite(rootFontSize) ? parsedWidth * rootFontSize : SIDEBAR_MIN_WIDTH_PX;
  }

  return parsedWidth;
}

type SidebarContextType = {
  state: 'expanded' | 'collapsed';
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: (text?: string, mode?: SidebarInputMode) => void;
  /**
   * Opens the sidebar if needed and sends `text` as a chat message. Null when the sidebar
   * hosts no chat, so callers can hide chat hand-offs instead of dropping them.
   */
  sendToSidebarChat: ((text: string) => void) | null;
  getSelectedText: () => string;
  sidebarWidth: string;
  setSidebarWidth: (width: string) => void;
  /** Ref to the sidebar wrapper div — used by SidebarRail to mutate --sidebar-width directly during drag */
  wrapperRef: React.RefObject<HTMLDivElement | null>;
};

type SidebarSide = 'left' | 'right';

type SidebarStateContextType = Pick<
  SidebarContextType,
  'state' | 'open' | 'openMobile' | 'isMobile' | 'sidebarWidth'
>;

type SidebarActionsContextType = Pick<
  SidebarContextType,
  | 'setOpen'
  | 'setOpenMobile'
  | 'toggleSidebar'
  | 'sendToSidebarChat'
  | 'getSelectedText'
  | 'setSidebarWidth'
  | 'wrapperRef'
>;

const SidebarStateContext = React.createContext<SidebarStateContextType | null>(null);
const SidebarActionsContext = React.createContext<SidebarActionsContextType | null>(null);

function useSidebarState() {
  const context = React.use(SidebarStateContext);

  if (!context) {
    throw new Error('useSidebarState must be used within a SidebarProvider.');
  }

  return context;
}

function useSidebarActions() {
  const context = React.use(SidebarActionsContext);

  if (!context) {
    throw new Error('useSidebarActions must be used within a SidebarProvider.');
  }

  return context;
}

function useSidebar() {
  const state = useSidebarState();
  const actions = useSidebarActions();

  return {
    ...state,
    ...actions,
  };
}

const SidebarProvider = ({
  ref,
  defaultOpen = true,
  defaultWidth = SIDEBAR_WIDTH,
  open: openProp,
  onOpenChange: setOpenProp,
  openCookieName = SIDEBAR_COOKIE_NAME,
  resetChatOnOpen = true,
  hasChat,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  defaultOpen?: boolean;
  /** Initial sidebar width (CSS value). Pass the request cookie from a server layout so SSR matches the persisted width. */
  defaultWidth?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  openCookieName?: string;
  resetChatOnOpen?: boolean;
  /** Whether this sidebar renders the AI chat; without one, `sendToSidebarChat` is null. */
  hasChat: boolean;
} & { ref?: React.RefObject<HTMLDivElement | null> }) => {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);
  const setSelectedTextAsInput = useChatStore((state) => state.setSelectedTextAsInput);
  const addPendingPrompt = useChatStore((state) => state.addPendingPrompt);
  const resetChat = useChatStore((state) => state.resetChat);
  const selectedText = useChatStore((state) => state.selectedText);

  const wrapperRef = React.useRef<HTMLDivElement>(null);

  const [sidebarWidth, setSidebarWidthState] = React.useState(defaultWidth);
  const setSidebarWidth = React.useCallback((width: string) => {
    setSidebarWidthState(width);
    document.cookie = `${SIDEBAR_WIDTH_COOKIE_NAME}=${width}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
  }, []);

  // This is the internal state of the sidebar.
  // We use openProp and setOpenProp for control from outside the component.
  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = useEventCallback((value: boolean | ((value: boolean) => boolean)) => {
    const openState = typeof value === 'function' ? value(open) : value;
    if (setOpenProp) {
      setOpenProp(openState);
    } else {
      _setOpen(openState);
    }
  });

  // Use an effect to update the cookie when open state changes
  React.useEffect(() => {
    document.cookie = `${openCookieName}=${open}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
  }, [open, openCookieName]);

  // Helper to toggle the sidebar.
  const toggleSidebar = useEventCallback((text?: string, mode: SidebarInputMode = 'replace') => {
    if (text) {
      const shouldResetChat = resetChatOnOpen && mode !== 'append';
      const isMobileCurrent = isMobile;
      const isSidebarOpen = open;
      const isSidebarOpenMobile = openMobile;

      // If text is provided and sidebar is closed, open it
      if (!isSidebarOpen && !isMobileCurrent) {
        if (shouldResetChat) {
          resetChat(); // Reset chat state first
        }
        setSelectedTextAsInput(text, mode); // Then set the new text
        setOpen(true);
        focusSidebarChatInput();
      } else if (!isSidebarOpenMobile && isMobileCurrent) {
        if (shouldResetChat) {
          resetChat(); // Reset chat state first
        }
        setSelectedTextAsInput(text, mode); // Then set the new text
        setOpenMobile(true);
        focusSidebarChatInput();
      } else {
        // Sidebar is already open, just update the text
        setSelectedTextAsInput(text, mode);
        focusSidebarChatInput();
      }
    } else {
      // If no text is provided, toggle the sidebar state and clear the text
      setSelectedTextAsInput('');
      if (isMobile) {
        const newState = !openMobile;
        setOpenMobile(newState);
        if (newState) {
          if (resetChatOnOpen) {
            resetChat(); // Reset chat state when opening
          }
          focusSidebarChatInput();
        }
      } else {
        const newState = !open;
        setOpen(newState);
        if (newState) {
          if (resetChatOnOpen) {
            resetChat(); // Reset chat state when opening
          }
          focusSidebarChatInput();
        }
      }
    }
  });

  // Sends a prompt to the sidebar chat without resetting the conversation,
  // opening the sidebar first when it is closed. The chat sends it once
  // mounted, queued behind any answer that is still streaming, and focuses its input.
  const sendToSidebarChat = useEventCallback((text: string) => {
    if (!text.trim()) {
      return;
    }

    addPendingPrompt(text);
    if (isMobile) {
      setOpenMobile(true);
    } else {
      setOpen(true);
    }
  });

  const getSelectedText = useEventCallback(() => selectedText);

  // We add a state so that we can do data-state="expanded" or "collapsed".
  // This makes it easier to style the sidebar with Tailwind classes.
  const state = open ? 'expanded' : 'collapsed';

  const stateContextValue = React.useMemo<SidebarStateContextType>(
    () => ({
      state,
      open,
      isMobile,
      openMobile,
      sidebarWidth,
    }),
    [state, open, isMobile, openMobile, sidebarWidth],
  );
  const actionsContextValue = React.useMemo<SidebarActionsContextType>(
    () => ({
      setOpen,
      setOpenMobile,
      toggleSidebar,
      sendToSidebarChat: hasChat ? sendToSidebarChat : null,
      getSelectedText,
      setSidebarWidth,
      wrapperRef,
    }),
    [
      setOpen,
      setOpenMobile,
      toggleSidebar,
      hasChat,
      sendToSidebarChat,
      getSelectedText,
      setSidebarWidth,
      wrapperRef,
    ],
  );

  return (
    <SidebarActionsContext value={actionsContextValue}>
      <SidebarStateContext value={stateContextValue}>
        <TooltipProvider delayDuration={0}>
          <div
            style={
              {
                ...style,
                '--sidebar-width': sidebarWidth,
                '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
              } as React.CSSProperties
            }
            className={cn(
              'group/sidebar-wrapper flex min-h-svh w-full has-data-[variant=inset]:bg-sidebar',
              className,
            )}
            ref={(node: HTMLDivElement | null) => {
              (wrapperRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
              if (ref) {
                ref.current = node;
              }
            }}
            {...props}
          >
            {children}
          </div>
        </TooltipProvider>
      </SidebarStateContext>
    </SidebarActionsContext>
  );
};
SidebarProvider.displayName = 'SidebarProvider';

const Sidebar = ({
  ref,
  side = 'left',
  mobileSide,
  mobileWidth = SIDEBAR_WIDTH_MOBILE,
  offsetDesktopHeader = false,
  variant = 'sidebar',
  collapsible = 'offcanvas',
  className,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  side?: SidebarSide;
  mobileSide?: SidebarSide;
  mobileWidth?: string;
  offsetDesktopHeader?: boolean;
  variant?: 'sidebar' | 'floating' | 'inset';
  collapsible?: 'offcanvas' | 'icon' | 'none';
} & { ref?: React.RefObject<HTMLDivElement | null> }) => {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

  if (collapsible === 'none') {
    return (
      <div
        className={cn(
          'flex h-full w-(--sidebar-width) flex-col bg-sidebar text-sidebar-foreground',
          className,
        )}
        ref={ref}
        {...props}
      >
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile} {...props}>
        <SheetContent
          data-sidebar="sidebar"
          data-mobile="true"
          className="w-(--sidebar-width) bg-sidebar p-0 text-sidebar-foreground sm:max-w-none [&>button]:hidden"
          style={
            {
              '--sidebar-width': mobileWidth,
            } as React.CSSProperties
          }
          side={mobileSide ?? side}
        >
          <div className="flex size-full flex-col">{children}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div
      ref={ref}
      className="group peer hidden text-sidebar-foreground md:block"
      data-state={state}
      data-collapsible={state === 'collapsed' ? collapsible : ''}
      data-variant={variant}
      data-side={side}
    >
      {/* This is what handles the sidebar gap on desktop */}
      <div
        className={cn(
          'relative w-(--sidebar-width) bg-sidebar transition-none',
          offsetDesktopHeader ? 'h-full' : 'h-svh',
          'group-data-[collapsible=offcanvas]:w-0',
          'group-data-[side=right]:rotate-180',
          variant === 'floating' || variant === 'inset'
            ? 'group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]'
            : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon)',
        )}
      />
      <div
        className={cn(
          'fixed z-50 hidden w-(--sidebar-width) transition-none md:flex',
          offsetDesktopHeader ? 'bottom-0 top-(--header-height)' : 'inset-y-0 h-svh',
          side === 'left'
            ? 'left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]'
            : 'right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]',
          // Adjust the padding for floating and inset variants.
          variant === 'floating' || variant === 'inset'
            ? 'p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]'
            : 'group-data-[collapsible=icon]:w-(--sidebar-width-icon) group-data-[side=left]:border-r group-data-[side=right]:border-l',
          className,
        )}
        {...props}
      >
        <div
          data-sidebar="sidebar"
          className="flex size-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow-sm"
        >
          {children}
        </div>
      </div>
    </div>
  );
};
Sidebar.displayName = 'Sidebar';

const SidebarTrigger = ({
  ref,
  className,
  onClick,
  hideWhenSidebarOpen = false,
  icon,
  ...props
}: React.ComponentProps<typeof Button> & { hideWhenSidebarOpen?: boolean } & {
  icon?: React.ReactNode;
  ref?: React.RefObject<React.ElementRef<typeof Button> | null>;
}) => {
  const { toggleSidebar, open, openMobile, isMobile } = useSidebar();

  const shouldHide = hideWhenSidebarOpen && (isMobile ? openMobile : open);

  return (
    <Button
      ref={ref}
      data-sidebar="trigger"
      variant="ghost"
      size="icon"
      className={cn('h-7 w-7 text-zinc-300 hover:text-zinc-50', className, shouldHide && 'hidden')}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    >
      {icon ?? <PanelRight />}
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
};
SidebarTrigger.displayName = 'SidebarTrigger';

/**
 * Draggable rail on the sidebar edge. Short click toggles collapse, drag resizes.
 * During drag, mutates `--sidebar-width` directly on the DOM wrapper to avoid
 * re-rendering the entire sidebar tree on every pointer move. React state is
 * only committed on pointer up.
 */
const SidebarRail = ({
  ref,
  disableToggle = false,
  className,
  ...props
}: React.ComponentProps<'button'> & {
  disableToggle?: boolean;
  ref?: React.RefObject<HTMLButtonElement | null>;
}) => {
  const { toggleSidebar, setSidebarWidth, wrapperRef } = useSidebarActions();
  const dragState = React.useRef<{
    startX: number;
    moved: number;
    side: 'left' | 'right';
    didStartDrag: boolean;
    startWidth: number | null;
    lastWidth: string | null;
    pendingWidth: string | null;
    frameId: number | null;
  } | null>(null);

  const beginRailDrag = React.useCallback(
    (clientX: number, target: HTMLButtonElement) => {
      if (dragState.current) {
        return;
      }

      const side = target.closest('[data-side]')?.getAttribute('data-side') as
        | 'left'
        | 'right'
        | null;
      if (!side || !wrapperRef.current) {
        return;
      }

      const maxWidthPx = window.innerWidth * (SIDEBAR_MAX_WIDTH_VW / 100);
      const wrapper = wrapperRef.current;
      dragState.current = {
        startX: clientX,
        moved: 0,
        side,
        didStartDrag: false,
        startWidth: null,
        lastWidth: null,
        pendingWidth: null,
        frameId: null,
      };

      const onDragMove = (moveEvent: MouseEvent | PointerEvent) => {
        const currentDragState = dragState.current;
        if (!currentDragState) {
          return;
        }

        const dx = moveEvent.clientX - currentDragState.startX;
        currentDragState.moved = Math.max(currentDragState.moved, Math.abs(dx));

        // Only pay the drag setup cost once the pointer actually moves far enough.
        if (!currentDragState.didStartDrag) {
          if (currentDragState.moved < SIDEBAR_DRAG_THRESHOLD_PX) {
            return;
          }

          currentDragState.startWidth = getSidebarStartWidthPx(wrapper);
          currentDragState.lastWidth = `${currentDragState.startWidth}px`;
          currentDragState.didStartDrag = true;
          wrapper.dataset.sidebarResizing = 'true';
          document.body.style.cursor = 'col-resize';
          document.body.style.userSelect = 'none';
        }

        if (currentDragState.startWidth == null) {
          return;
        }

        // Right sidebar: dragging left (negative dx) = wider
        const delta = currentDragState.side === 'right' ? -dx : dx;
        const newWidth = Math.min(
          maxWidthPx,
          Math.max(SIDEBAR_MIN_WIDTH_PX, currentDragState.startWidth + delta),
        );
        const widthStr = `${Math.round(newWidth)}px`;
        currentDragState.lastWidth = widthStr;

        // Collapse resize writes to a single DOM mutation per frame.
        currentDragState.pendingWidth = widthStr;
        if (currentDragState.frameId != null) {
          return;
        }

        currentDragState.frameId = window.requestAnimationFrame(() => {
          const activeDragState = dragState.current;
          const nextWidth = activeDragState?.pendingWidth;
          if (!activeDragState || !nextWidth) {
            return;
          }

          wrapper.style.setProperty('--sidebar-width', nextWidth);
          activeDragState.pendingWidth = null;
          activeDragState.frameId = null;
        });
      };

      const onDragEnd = () => {
        document.removeEventListener('pointermove', onDragMove);
        document.removeEventListener('pointerup', onDragEnd);
        document.removeEventListener('pointercancel', onDragEnd);
        document.removeEventListener('mousemove', onDragMove);
        document.removeEventListener('mouseup', onDragEnd);

        const currentDragState = dragState.current;
        if (currentDragState?.didStartDrag) {
          if (currentDragState.frameId != null) {
            window.cancelAnimationFrame(currentDragState.frameId);
            currentDragState.frameId = null;
          }
          if (currentDragState.lastWidth) {
            wrapper.style.setProperty('--sidebar-width', currentDragState.lastWidth);
          }
          delete wrapper.dataset.sidebarResizing;
          document.body.style.cursor = '';
          document.body.style.userSelect = '';
        }

        const wasDrag = Boolean(currentDragState?.didStartDrag);
        const finalWidth = currentDragState?.lastWidth;
        dragState.current = null;

        if (wasDrag && finalWidth) {
          // Commit final width to React state (triggers cookie persistence)
          setSidebarWidth(finalWidth);
        } else if (!disableToggle) {
          toggleSidebar();
        }
      };

      document.addEventListener('pointermove', onDragMove);
      document.addEventListener('pointerup', onDragEnd);
      document.addEventListener('pointercancel', onDragEnd);
      document.addEventListener('mousemove', onDragMove);
      document.addEventListener('mouseup', onDragEnd);
    },
    [disableToggle, setSidebarWidth, toggleSidebar, wrapperRef],
  );

  const handlePointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture?.(e.pointerId);
      beginRailDrag(e.clientX, e.currentTarget);
    },
    [beginRailDrag],
  );

  const handleMouseDown = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      beginRailDrag(e.clientX, e.currentTarget);
    },
    [beginRailDrag],
  );

  return (
    <button
      ref={ref}
      type="button"
      data-sidebar="rail"
      aria-label={disableToggle ? 'Resize Sidebar' : 'Toggle Sidebar'}
      tabIndex={-1}
      onPointerDown={handlePointerDown}
      onMouseDown={handleMouseDown}
      title={disableToggle ? 'Resize Sidebar' : 'Toggle Sidebar'}
      className={cn(
        'absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear [[data-sidebar-resizing=true]_&]:transition-none after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] hover:after:bg-sidebar-border group-data-[side=left]:-right-4 group-data-[side=right]:left-0 sm:flex',
        'in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize',
        '[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize',
        'group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full hover:group-data-[collapsible=offcanvas]:bg-sidebar',
        '[[data-side=left][data-collapsible=offcanvas]_&]:-right-2',
        '[[data-side=right][data-collapsible=offcanvas]_&]:-left-2',
        className,
      )}
      {...props}
    />
  );
};
SidebarRail.displayName = 'SidebarRail';

const SidebarInset = ({
  ref,
  className,
  ...props
}: React.ComponentProps<'main'> & { ref?: React.RefObject<HTMLDivElement | null> }) => {
  return (
    <main
      ref={ref}
      className={cn(
        'relative flex min-h-svh flex-1 flex-col bg-background',
        'peer-data-[variant=inset]:min-h-[calc(100svh-(--spacing(4)))] md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow-sm',
        className,
      )}
      {...props}
    />
  );
};
SidebarInset.displayName = 'SidebarInset';

const SidebarInput = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof Input> & {
  ref?: React.RefObject<React.ElementRef<typeof Input> | null>;
}) => {
  return (
    <Input
      ref={ref}
      data-sidebar="input"
      className={cn(
        'h-8 w-full bg-background shadow-none focus-visible:ring-2 focus-visible:ring-sidebar-ring',
        className,
      )}
      {...props}
    />
  );
};
SidebarInput.displayName = 'SidebarInput';

const SidebarHeader = ({
  ref,
  className,
  ...props
}: React.ComponentProps<'div'> & { ref?: React.RefObject<HTMLDivElement | null> }) => {
  return (
    <div
      ref={ref}
      data-sidebar="header"
      className={cn('flex flex-col gap-2 p-2', className)}
      {...props}
    />
  );
};
SidebarHeader.displayName = 'SidebarHeader';

const SidebarFooter = ({
  ref,
  className,
  ...props
}: React.ComponentProps<'div'> & { ref?: React.RefObject<HTMLDivElement | null> }) => {
  return (
    <div
      ref={ref}
      data-sidebar="footer"
      className={cn('flex flex-col gap-2 p-2', className)}
      {...props}
    />
  );
};
SidebarFooter.displayName = 'SidebarFooter';

const SidebarSeparator = ({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof Separator> & {
  ref?: React.RefObject<React.ElementRef<typeof Separator> | null>;
}) => {
  return (
    <Separator
      ref={ref}
      data-sidebar="separator"
      className={cn('mx-2 w-auto bg-sidebar-border', className)}
      {...props}
    />
  );
};
SidebarSeparator.displayName = 'SidebarSeparator';

const SidebarContent = ({
  ref,
  className,
  ...props
}: React.ComponentProps<'div'> & { ref?: React.RefObject<HTMLDivElement | null> }) => {
  return (
    <div
      ref={ref}
      data-sidebar="content"
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-2 overflow-auto group-data-[collapsible=icon]:overflow-hidden',
        className,
      )}
      {...props}
    />
  );
};
SidebarContent.displayName = 'SidebarContent';

const SidebarGroup = ({
  ref,
  className,
  ...props
}: React.ComponentProps<'div'> & { ref?: React.RefObject<HTMLDivElement | null> }) => {
  return (
    <div
      ref={ref}
      data-sidebar="group"
      className={cn('relative flex w-full min-w-0 flex-col', className)}
      {...props}
    />
  );
};
SidebarGroup.displayName = 'SidebarGroup';

const SidebarGroupLabel = ({
  ref,
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'div'> & { asChild?: boolean } & {
  ref?: React.RefObject<HTMLDivElement | null>;
}) => {
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      ref={ref}
      data-sidebar="group-label"
      className={cn(
        'duration-200 flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 outline-hidden ring-sidebar-ring transition-[margin,opacity] ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
        'group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0',
        className,
      )}
      {...props}
    />
  );
};
SidebarGroupLabel.displayName = 'SidebarGroupLabel';

const SidebarGroupAction = ({
  ref,
  className,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> & { asChild?: boolean } & {
  ref?: React.RefObject<HTMLButtonElement | null>;
}) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref}
      data-sidebar="group-action"
      className={cn(
        'absolute right-3 top-3.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-hidden ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0',
        // Increases the hit area of the button on mobile.
        'after:absolute after:-inset-2 md:after:hidden',
        'group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  );
};
SidebarGroupAction.displayName = 'SidebarGroupAction';

const SidebarGroupContent = ({
  ref,
  className,
  ...props
}: React.ComponentProps<'div'> & { ref?: React.RefObject<HTMLDivElement | null> }) => (
  <div
    ref={ref}
    data-sidebar="group-content"
    className={cn('w-full text-sm', className)}
    {...props}
  />
);
SidebarGroupContent.displayName = 'SidebarGroupContent';

const SidebarMenu = ({
  ref,
  className,
  ...props
}: React.ComponentProps<'ul'> & { ref?: React.RefObject<HTMLUListElement | null> }) => (
  <ul
    ref={ref}
    data-sidebar="menu"
    className={cn('flex w-full min-w-0 flex-col gap-1', className)}
    {...props}
  />
);
SidebarMenu.displayName = 'SidebarMenu';

const SidebarMenuItem = ({
  ref,
  className,
  ...props
}: React.ComponentProps<'li'> & { ref?: React.RefObject<HTMLLIElement | null> }) => (
  <li
    ref={ref}
    data-sidebar="menu-item"
    className={cn('group/menu-item relative', className)}
    {...props}
  />
);
SidebarMenuItem.displayName = 'SidebarMenuItem';

const sidebarMenuButtonVariants = cva(
  'peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-hidden ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-3.5! group-data-[collapsible=icon]:p-2! [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        outline:
          'bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]',
      },
      size: {
        default: 'h-8 text-sm',
        sm: 'h-7 text-xs',
        lg: 'h-12 text-sm group-data-[collapsible=icon]:p-0!',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

const SidebarMenuButton = ({
  ref,
  asChild = false,
  isActive = false,
  variant = 'default',
  size = 'default',
  tooltip,
  className,
  ...props
}: React.ComponentProps<'button'> & {
  asChild?: boolean;
  isActive?: boolean;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
} & VariantProps<typeof sidebarMenuButtonVariants> & {
    ref?: React.RefObject<HTMLButtonElement | null>;
  }) => {
  const Comp = asChild ? Slot : 'button';
  const { isMobile, state } = useSidebar();

  const button = (
    <Comp
      ref={ref}
      data-sidebar="menu-button"
      data-size={size}
      data-active={isActive}
      className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
      {...props}
    />
  );

  if (!tooltip) {
    return button;
  }

  if (typeof tooltip === 'string') {
    tooltip = {
      children: tooltip,
    };
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== 'collapsed' || isMobile}
        {...tooltip}
      />
    </Tooltip>
  );
};
SidebarMenuButton.displayName = 'SidebarMenuButton';

const SidebarMenuAction = ({
  ref,
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: React.ComponentProps<'button'> & {
  asChild?: boolean;
  showOnHover?: boolean;
} & { ref?: React.RefObject<HTMLButtonElement | null> }) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref}
      data-sidebar="menu-action"
      className={cn(
        'absolute right-1 top-1.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-hidden ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 peer-hover/menu-button:text-sidebar-accent-foreground [&>svg]:size-4 [&>svg]:shrink-0',
        // Increases the hit area of the button on mobile.
        'after:absolute after:-inset-2 md:after:hidden',
        'peer-data-[size=sm]/menu-button:top-1',
        'peer-data-[size=default]/menu-button:top-1.5',
        'peer-data-[size=lg]/menu-button:top-2.5',
        'group-data-[collapsible=icon]:hidden',
        showOnHover &&
          'group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 peer-data-[active=true]/menu-button:text-sidebar-accent-foreground md:opacity-0',
        className,
      )}
      {...props}
    />
  );
};
SidebarMenuAction.displayName = 'SidebarMenuAction';

const SidebarMenuBadge = ({
  ref,
  className,
  ...props
}: React.ComponentProps<'div'> & { ref?: React.RefObject<HTMLDivElement | null> }) => (
  <div
    ref={ref}
    data-sidebar="menu-badge"
    className={cn(
      'absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums text-sidebar-foreground select-none pointer-events-none',
      'peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground',
      'peer-data-[size=sm]/menu-button:top-1',
      'peer-data-[size=default]/menu-button:top-1.5',
      'peer-data-[size=lg]/menu-button:top-2.5',
      'group-data-[collapsible=icon]:hidden',
      className,
    )}
    {...props}
  />
);
SidebarMenuBadge.displayName = 'SidebarMenuBadge';

const SidebarMenuSkeleton = ({
  ref,
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<'div'> & {
  showIcon?: boolean;
} & { ref?: React.RefObject<HTMLDivElement | null> }) => {
  // Generate stable "random" width based on component's unique ID
  const id = React.useId();
  const width = React.useMemo(() => {
    // Simple hash of the id to get a deterministic "random" width between 50-90%
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
      hash = (hash << 5) - hash + id.charCodeAt(i);
      hash |= 0;
    }
    return `${Math.abs(hash % 40) + 50}%`;
  }, [id]);

  return (
    <div
      ref={ref}
      data-sidebar="menu-skeleton"
      className={cn('rounded-md h-8 flex gap-2 px-2 items-center', className)}
      {...props}
    >
      {showIcon && <Skeleton className="size-4 rounded-md" data-sidebar="menu-skeleton-icon" />}
      <Skeleton
        className="h-4 max-w-(--skeleton-width) flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            '--skeleton-width': width,
          } as React.CSSProperties
        }
      />
    </div>
  );
};
SidebarMenuSkeleton.displayName = 'SidebarMenuSkeleton';

const SidebarMenuSub = ({
  ref,
  className,
  ...props
}: React.ComponentProps<'ul'> & { ref?: React.RefObject<HTMLUListElement | null> }) => (
  <ul
    ref={ref}
    data-sidebar="menu-sub"
    className={cn(
      'mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l border-sidebar-border px-2.5 py-0.5',
      'group-data-[collapsible=icon]:hidden',
      className,
    )}
    {...props}
  />
);
SidebarMenuSub.displayName = 'SidebarMenuSub';

const SidebarMenuSubItem = ({
  ref,
  ...props
}: React.ComponentProps<'li'> & { ref?: React.RefObject<HTMLLIElement | null> }) => (
  <li ref={ref} {...props} />
);
SidebarMenuSubItem.displayName = 'SidebarMenuSubItem';

const SidebarMenuSubButton = ({
  ref,
  asChild = false,
  size = 'md',
  isActive,
  className,
  ...props
}: React.ComponentProps<'a'> & {
  asChild?: boolean;
  size?: 'sm' | 'md';
  isActive?: boolean;
} & { ref?: React.RefObject<HTMLAnchorElement | null> }) => {
  const Comp = asChild ? Slot : 'a';

  return (
    <Comp
      ref={ref}
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        'flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-md px-2 text-sidebar-foreground outline-hidden ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0 [&>svg]:text-sidebar-accent-foreground',
        'data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground',
        size === 'sm' && 'text-xs',
        size === 'md' && 'text-sm',
        'group-data-[collapsible=icon]:hidden',
        className,
      )}
      {...props}
    />
  );
};
SidebarMenuSubButton.displayName = 'SidebarMenuSubButton';

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
  useSidebarActions,
  useSidebarState,
};
